<template>
  <div class="graphic" ref="el">
    <svg
      v-if="w > 0"
      :viewBox="`0 0 ${w} 432`"
      width="100%"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      overflow="visible"
      aria-hidden="true"
    >
      <path
        :d="d"
        stroke-width="15.5"
        stroke-linejoin="miter"
        stroke-miterlimit="10"
        stroke-linecap="square"
        fill="none"
      />
    </svg>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useElementSize } from '@vueuse/core'

const el = ref(null)
const { width: w } = useElementSize(el)

/*
 * The header graphic is a single connected thick line.
 * Waypoints describe the centerline; stroke + miter joins reproduce
 * the bevel-cut bar ends and filled outer corners automatically.
 *
 * Coordinate system: SVG x=0 is the left edge of the content area
 * (i.e. after --offset padding). The bracket shape lives in the
 * rightmost 612 units, so all bracket coordinates are offset by (w - 612).
 */
const d = computed(() => {
  const o = w.value - 612 // bracket x-offset from SVG left edge

  const pts = [
    // ── Topbar (horizontal bar running left to right) ──
    [0,          7.99],
    // ── Turn: topbar → thin vertical going down ──
    [o + 368,   11.80],
    // ── Thin vertical ends → turn right along top horizontal ──
    [o + 368,  127.12],
    // ── Top horizontal ends → turn down along right vertical ──
    [o + 590,  132.97],
    // ── Right vertical ends → turn left along mid horizontal ──
    [o + 604,  200.56],
    // ── Mid horizontal ends → turn down along lower vertical ──
    [o + 514,  251.88],
    // ── Lower vertical ends → turn right along bottom horizontal ──
    [o + 419,  345.42],
    // ── Bottom horizontal ends → connect to diagonal ──
    [o + 419,  424.09],
    [o + 569,  409.19],
    // ── Diagonal runs from bottom-right to left ──
    [o +  65,  251.38],
    // ── Turn: diagonal → left-mid vertical going up ──
    [o +  52,  159.99],
    // ── Turn: left-mid vertical → short connector going left ──
    [o +  37,  147.55],
    // ── Turn: connector → left vertical going down ──
    [o +  21,  161.98],
    // ── Left vertical end ──
    [o +  21,  411.99],
  ]

  return 'M ' + pts.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join(' L ')
})
</script>

<style scoped>
.graphic {
  width: 100%;
  overflow: visible;
  line-height: 0;
}

svg {
  display: block;
  overflow: visible;
}

path {
  stroke: var(--fill-0, black);
}
</style>
